import React, { useContext } from 'react';
import { MessageSnackbar } from '@lialo/pwa-ui/lib/Snackbar/MessageSnackbar';
import { useTranslation } from 'react-i18next';

interface Props {
  children: React.ReactNode;
}

export interface SnackbarConfig {
  key: string;
  params?: { [key: string]: any };
}

interface SnackbarState {
  showSnackbar: (config: SnackbarConfig) => void;
}

const SnackbarContext = React.createContext<SnackbarState | null>(null);

export const SnackbarProvider = ({ children }: Props) => {
  const [snackbarConfig, setSnackbarConfig] = React.useState<SnackbarConfig | null>(null);
  const { t } = useTranslation();

  const onClose = () => {
    setSnackbarConfig(null);
  };

  const showSnackbar = React.useCallback(
    (config: SnackbarConfig) => {
      setSnackbarConfig(config);
    },
    [setSnackbarConfig]
  );

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <MessageSnackbar
        handleClose={onClose}
        open={snackbarConfig !== null}
        translation={snackbarConfig ? t(snackbarConfig.key, snackbarConfig.params) : undefined}
      />
    </SnackbarContext.Provider>
  );
};

export function useShowSnackbar() {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('SnackbarContext is null, wrap your component with SnackbarProvider');
  }
  return context.showSnackbar;
}

export function withSnackbarProvider<P extends object>(Component: React.FunctionComponent<P>): React.FunctionComponent<P> {
  return (props: P) => (
    <SnackbarProvider>
      <Component {...props} />
    </SnackbarProvider>
  );
}
